/**
 * Thin typed helpers over the app's global axios instance (baseURL, auth header,
 * validateStatus < 500 and the response interceptor that resolves with `res.data`
 * are configured in main.ts). Response shapes are exactly what the endpoints return.
 *
 * Read-request de-duplication/caching (utils/requestDeduplication) is a global axios
 * interceptor; mutations in the api modules call `invalidate(pattern)` so cached reads
 * of the same resource are dropped (a no-op while that interceptor is not installed).
 */
import axios from "axios";
import type { AxiosRequestConfig } from "axios";
import { clearCache } from "@/utils/requestDeduplication";

/** axios config plus the app-level `skipSwal` flag (declared in main.ts). */
export type ApiConfig = AxiosRequestConfig & { skipSwal?: boolean; skipDeduplication?: boolean };

export function apiGet<T = any>(url: string, config?: ApiConfig): Promise<T> {
  return axios.get(url, config) as unknown as Promise<T>;
}

export function apiPost<T = any>(url: string, data?: unknown, config?: ApiConfig): Promise<T> {
  return axios.post(url, data, config) as unknown as Promise<T>;
}

export function apiPut<T = any>(url: string, data?: unknown, config?: ApiConfig): Promise<T> {
  return axios.put(url, data, config) as unknown as Promise<T>;
}

export function apiPatch<T = any>(url: string, data?: unknown, config?: ApiConfig): Promise<T> {
  return axios.patch(url, data, config) as unknown as Promise<T>;
}

export function apiDelete<T = any>(url: string, config?: ApiConfig): Promise<T> {
  return axios.delete(url, config) as unknown as Promise<T>;
}

/** Drop cached reads whose key contains `pattern`. */
export function invalidate(pattern: string): void {
  clearCache(pattern);
}
